import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { exportToExcel } from '../../helpers/exportFuncs';
import { FilterContext } from '../../contexts/FilterContext';
import LoadingSpinner from '../LoadingSpinner';
import moment from 'moment';

interface RoiNetworkMonthlyData {
  network: string;
  year: number;
  month: number;
  leads: number;
  total_cost: number;
  revenue_30d: number;
  roi30: number | null;
}

interface MonthlyDataMap {
  [network: string]: {
    [monthKey: string]: {
      roi30: number | null;
      leads: number;
    };
  };
}

const RoiNetworkMonthlySummary: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const [data, setData] = useState<RoiNetworkMonthlyData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const selectedNetworks = Array.isArray(filterState.network)
      ? filterState.network
      : filterState.network
        ? [filterState.network]
        : [];

    if (selectedNetworks.length === 0) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const fetchRoiData = async () => {
      try {
        const networks = Array.isArray(filterState.network)
          ? filterState.network
          : [filterState.network];
        const themes = Array.isArray(filterState.theme)
          ? filterState.theme
          : filterState.theme
            ? [filterState.theme]
            : [];
        const options = {
          network: networks,
          ...(themes.length > 0 && { theme: themes }),
          ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
        };

        const roiData = (await fetchData<Record<string, any>[]>(
          '/api/vnr/roi-network-monthly-summary',
          options,
        )) as Record<string, any>[];

        const typedData: RoiNetworkMonthlyData[] = roiData.map((item) => ({
          network: item.network,
          year: parseInt(item.year),
          month: parseInt(item.month),
          leads: parseInt(item.leads) || 0,
          total_cost: parseFloat(item.total_cost) || 0,
          revenue_30d: parseFloat(item.revenue_30d) || 0,
          roi30: item.roi30 ? parseFloat(item.roi30) : null,
        }));

        setData(typedData);
        setLoading(false);
      } catch (err: any) {
        setError(err);
        setLoading(false);
      }
    };

    fetchRoiData();
  }, [filterState.network, filterState.theme, filterState.vnrVerlag]);

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (data.length === 0) {
    return null;
  }

  const last6Months: string[] = [];
  for (let i = 5; i >= 0; i--) {
    const monthDate = moment().subtract(i, 'months');
    last6Months.push(monthDate.format('YYYY-MM'));
  }

  const monthlyDataMap: MonthlyDataMap = {};

  data.forEach((item) => {
    const monthKey = `${item.year}-${String(item.month).padStart(2, '0')}`;

    if (!monthlyDataMap[item.network]) {
      monthlyDataMap[item.network] = {};
    }

    monthlyDataMap[item.network][monthKey] = {
      roi30: item.roi30,
      leads: item.leads,
    };
  });

  const networks = Object.keys(monthlyDataMap).sort();

  // Insgesamt je Monat: Leads summiert, ROI kostengewichtet (Σ Umsatz 30d / Σ Kosten) —
  // nicht der Mittelwert der Netzwerk-ROIs, sonst zaehlte ein kleines Netzwerk wie ein grosses.
  const monthlyTotals: Record<string, { roi30: number | null; leads: number }> = {};
  last6Months.forEach((monthKey) => {
    const rows = data.filter(
      (item) => `${item.year}-${String(item.month).padStart(2, '0')}` === monthKey,
    );
    const leads = rows.reduce((sum, item) => sum + item.leads, 0);
    const cost = rows.reduce((sum, item) => sum + item.total_cost, 0);
    const revenue30d = rows.reduce((sum, item) => sum + item.revenue_30d, 0);
    monthlyTotals[monthKey] = { roi30: cost > 0 ? (revenue30d / cost) * 100 : null, leads };
  });

  const formatCellValue = (roi30: number | null, leads: number): string => {
    if (roi30 === null || leads === 0) {
      return '-';
    }
    return `${formatNumber(roi30, 2)}% (${formatNumber(leads, 0)})`;
  };

  const getMonthHeader = (monthKey: string): string => {
    const [year, month] = monthKey.split('-');
    return moment(`${year}-${month}`, 'YYYY-MM').format('MMM YYYY');
  };

  const prepareExcelData = () => {
    const totalRow: any = { Netzwerk: 'Insgesamt' };
    last6Months.forEach((monthKey) => {
      totalRow[getMonthHeader(monthKey)] = formatCellValue(
        monthlyTotals[monthKey].roi30,
        monthlyTotals[monthKey].leads,
      );
    });
    const networkRows = networks.map((network) => {
      const row: any = { Netzwerk: network };
      last6Months.forEach((monthKey) => {
        const monthData = monthlyDataMap[network][monthKey];
        const monthHeader = getMonthHeader(monthKey);
        if (monthData) {
          row[monthHeader] = formatCellValue(monthData.roi30, monthData.leads);
        } else {
          row[monthHeader] = '-';
        }
      });
      return row;
    });
    return [...networkRows, totalRow];
  };

  return (
    <Box bgcolor="#fff">
      <Box>
        <Button
          onClick={() => exportToExcel(prepareExcelData(), 'ROI30-Netzwerk-Monatsuebersicht')}
          sx={{ float: 'right' }}
        >
          Download als Excel
        </Button>
      </Box>
      <Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Netzwerk</TableCell>
                {last6Months.map((monthKey) => (
                  <TableCell key={monthKey} sx={{ fontWeight: 'bold' }}>
                    {getMonthHeader(monthKey)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {networks.map((network, index) => (
                <TableRow
                  key={network}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <TableCell>{network}</TableCell>
                  {last6Months.map((monthKey) => {
                    const monthData = monthlyDataMap[network][monthKey];
                    return (
                      <TableCell key={monthKey}>
                        {monthData ? formatCellValue(monthData.roi30, monthData.leads) : '-'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Insgesamt</TableCell>
                {last6Months.map((monthKey) => (
                  <TableCell key={monthKey} sx={{ fontWeight: 'bold' }}>
                    {formatCellValue(monthlyTotals[monthKey].roi30, monthlyTotals[monthKey].leads)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default RoiNetworkMonthlySummary;
